import { useContext, useState } from 'react';
import { EffectiveDateInput } from '../components/effectiveDateInput';
import { Button } from '@/components/ui/button';
import { toDateString } from '../../../utils/dates';

import {
  glacierPolicyApiCreateMidtermTransactionMutation,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { TransactionType, type Policy } from '@/lib/heyapi/types.gen';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { useParams } from 'react-router';
import { PermissionsContext } from '../../PermissionContext';
import { userRoleEnum } from '../../types';

export function EndorsementForm({ policy }: { policy: Policy | undefined }) {
  const [newEffectiveDate, setNewEffectiveDate] = useState('');
  const { role, refetchAgentAuthority } = useContext(PermissionsContext);
  const { id } = useParams();

  const queryClient = useQueryClient();

  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEffectiveDate(e.target.value);
  };

  const createMidterm = useMutation({
    ...glacierPolicyApiCreateMidtermTransactionMutation(),
    onError: (error) => {
      toast({
        title: 'Error',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
            <code className="text-white">{error as string}</code>
          </pre>
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      if (role === userRoleEnum.AGENT) {
        refetchAgentAuthority();
      }
      queryClient.refetchQueries({ queryKey: qk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Loading New Midterm Transaction</code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/policy-details`;
      }, 500);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    createMidterm.mutate({
      body: {
        effective_date: new Date(newEffectiveDate),
        transaction_type: TransactionType.POLICY_ENDORSEMENT,
      },
      path: {
        account_id: id!,
      },
    });
  };

  // Turn the date into a string and replace the hyphens with slashes
  // to make it compatible with the Date constructor
  let minEffectiveString = String(policy?.details?.policyEffectiveDate).replace(
    /-/g,
    '/'
  );

  const effectiveDateMinimum = toDateString(new Date(minEffectiveString));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      <EffectiveDateInput
        value={newEffectiveDate}
        onChange={handleInputChange}
        orientation="horizontal"
        label="Effective date"
        min={effectiveDateMinimum ? effectiveDateMinimum : undefined}
        max={
          policy?.details?.policyExpirationDate
            ? toDateString(policy?.details?.policyExpirationDate)
            : undefined
        }
        required
        stretch
      />
      <Button type="submit">Confirm</Button>
    </form>
  );
}
